import React from 'react';

import type { HubItem } from '../api/types';
import { ArrowLeft, ExternalLink, EyeOff } from 'lucide-react';

import HubItemRow from './HubItemRow';
import ShadowContent from './ShadowContent';
import Spinner from './Spinner';
import { useHubItemContent } from '../hooks/useHubItemContent';
import { buildArticlePath, buildNewsPath } from '../utils/contentPaths';

// The hub renders an article and a news item the same way; only the wording around them
// differs, since one is a library and the other a feed.
const COPY = {
  article: {
    listing: 'Library row',
    listingHint: 'How this article appears in the article library.',
    backLabel: 'All articles',
    empty: 'This article has no content yet.',
    externalLead: 'This article lives on another site. The hub shows the row and links out:',
  },
  news: {
    listing: 'Feed row',
    listingHint: 'How this item appears in the news feed.',
    backLabel: 'All news',
    empty: 'This news item has no content yet.',
    externalLead: 'This item lives on another site. The hub shows the row and links out:',
  },
} as const;

const formatDate = (iso: string): string => {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

/** The One Label Rule's single label style, used here to name each half of the preview. */
const SECTION_LABEL =
  'font-ui text-step--2 font-medium uppercase tracking-[0.18em] text-ink-muted';

interface HubItemPreviewProps {
  item: HubItem;
}

/**
 * Shows an article or news item as the info hub will present it: the row a reader meets in
 * the listing, then the reading view they land on.
 *
 * It lives in this module rather than in the admin screens on purpose — the hub is the
 * authority on what its own content looks like, so the preview and the public route render
 * from one definition instead of drifting apart. (Same reasoning as the URL builders the admin
 * forms already import from here.)
 *
 * ONE THING TO EXPECT UNTIL STEPS 6-7. This is the only part of the module that renders inside
 * *portal* chrome — an admin dialog, which is still on the app's old white/gray palette. The
 * preview follows whichever ground is live, because that is what the hub will actually look
 * like and a preview that lied about it would be worthless. So on the navy ground it renders
 * as a navy panel inside a white dialog. That mismatch is the portal's, not the hub's, and it
 * closes when the portal comes onto this system.
 */
const HubItemPreview: React.FC<HubItemPreviewProps> = ({ item }) => {
  const { html, pictureUrl, state } = useHubItemContent(item);
  const copy = COPY[item.kind];
  const path = item.kind === 'article' ? buildArticlePath(item.slug) : buildNewsPath(item.slug);
  const dateLabel = formatDate(item.date);
  const meta = [dateLabel, item.categoryTitle].filter(Boolean).join(' · ');

  if (state === 'loading') {
    return (
      <div className="flex min-h-[16rem] items-center justify-center bg-page">
        <Spinner size="lg" />
      </div>
    );
  }

  if (state === 'notfound' || state === 'error') {
    return (
      <div className="min-h-[16rem] bg-page p-8">
        <h2 className="font-display text-step-2 text-ink">
          {state === 'notfound' ? 'Nothing to preview' : 'Something went wrong'}
        </h2>
        <p className="mt-3 text-step--1 text-ink-muted">
          {state === 'notfound'
            ? 'This record no longer exists.'
            : 'The content couldn’t be loaded. Please try again later.'}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-page p-1 font-ui text-ink">
      {!item.isActive && (
        // A note on a hairline rather than a coloured chip. The system has exactly one
        // semantic colour — danger — and this is not an error; it is a state. Amber would
        // have been a third accent, which DESIGN.md rules out.
        <p className="mb-8 flex items-start gap-2 border-l border-rule-strong pl-4 text-step--1 text-ink-muted">
          <EyeOff size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
          Deactivated — readers can’t see this in the hub yet. This is how it will look once it’s
          activated.
        </p>
      )}

      {/* The listing row. Summary and picture are listing-only fields, so an author can't
          judge them from the reading view alone. */}
      <section className="mb-12">
        <p className={SECTION_LABEL}>{copy.listing}</p>
        <p className="mb-4 mt-1 text-step--1 text-ink-muted">{copy.listingHint}</p>

        {/* The listing's own component, not a copy of it — so what an author sees here can't
            drift from what the hub actually renders. */}
        <div className="border-y border-rule">
          <HubItemRow item={{ ...item, pictureUrl }} interactive={false} />
        </div>

        {!item.summary && (
          <p className="mt-3 text-step--1 text-ink-muted">
            No summary — the listing shows the title and date only.
          </p>
        )}
      </section>

      {/* The reading view. Mirrors the newsletter detail page — the hub's established shape
          for "one dated thing, read start to finish". */}
      <section>
        <p className={`${SECTION_LABEL} mb-4`}>Reading view</p>

        <article className="max-w-measure border border-rule-strong px-6 py-8 sm:px-10">
          <span className="mb-8 inline-flex items-center gap-1.5 text-step--1 text-ink-muted">
            <ArrowLeft size={15} aria-hidden="true" />
            {copy.backLabel}
          </span>

          <header className="border-b border-rule-strong pb-6">
            <h1 className="font-display text-step-3 leading-[1.05] tracking-[-0.02em] text-ink">
              {item.title}
            </h1>
            {meta && <p className="mt-3 text-step--1 text-ink-muted">{meta}</p>}
            <p className="mt-2 text-step--2 text-ink-muted">{path}</p>
          </header>

          {pictureUrl && (
            <figure className="mt-8 border border-rule-strong">
              <img src={pictureUrl} alt="" className="max-h-96 w-full object-cover" />
            </figure>
          )}

          <div className="mt-8">
            {item.externalUrl ? (
              <div className="border-l border-rule-strong pl-5 text-step--1 text-ink-muted">
                <p>{copy.externalLead}</p>
                <p className="mt-2 flex items-center gap-1.5 break-all text-ink">
                  <ExternalLink size={15} aria-hidden="true" className="shrink-0" />
                  {item.externalUrl}
                </p>
              </div>
            ) : html ? (
              // Shadow root, as everywhere else the hub renders authored HTML: Unlayer's own
              // <style> blocks are unscoped and would otherwise restyle the admin chrome
              // around this dialog.
              <ShadowContent html={html} />
            ) : (
              <p className="text-step-0 text-ink-muted">{copy.empty}</p>
            )}
          </div>
        </article>
      </section>
    </div>
  );
};

export default HubItemPreview;
